import { lazy, useRef, type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { ClientSuspense } from 'dibs-elements/exports/ClientSuspense';
import { useClientState } from 'dibs-react-hooks/src/useClientState';

import { PdpRespMarketplaceLoadingCarousel } from '../respMarketplace/PdpRespMarketplaceLoadingCarousel/PdpRespMarketplaceLoadingCarousel';
import VisibilityTracker from 'dibs-visibility-tracker/exports/VisibilityTracker';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { getAbTestV2, trackAbTestV2Variant } from 'dibs-ab-tests/src/clientAbTestV2';
import { SPONSORED_LISTING_ON_PDP } from '../../utils/abTestHelper';

import { type PdpSharedFeaturedRelatedItemsCarouselLazy_viewer$key } from './__generated__/PdpSharedFeaturedRelatedItemsCarouselLazy_viewer.graphql';
import { type PdpSharedFeaturedRelatedItemsCarouselLazy_user$key } from './__generated__/PdpSharedFeaturedRelatedItemsCarouselLazy_user.graphql';

const PdpSharedFeaturedRelatedItemsCarousel = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpSharedFeaturedRelatedItemsCarousel'" */ './PdpSharedFeaturedRelatedItemsCarousel'
        )
);

type Props = {
    viewer: PdpSharedFeaturedRelatedItemsCarouselLazy_viewer$key;
    user: PdpSharedFeaturedRelatedItemsCarouselLazy_user$key | null | undefined;
    currency: string;
    afterDisplay?: boolean | null;
};

const viewerFragment = graphql`
    fragment PdpSharedFeaturedRelatedItemsCarouselLazy_viewer on Viewer {
        ...PdpSharedFeaturedRelatedItemsCarousel_viewer
        sponsoredItems(
            userId: $userId
            guestId: $guestId
            itemId: $itemId
            isTrade: $isTrade
            priceBookName: $priceBookName
            userCountryCode: $userCountryCode
            pageSize: 10
        ) {
            shouldFireTracking
            ...PdpSharedFeaturedRelatedItemsCarousel_sponsoredItems
        }
    }
`;

const userFragment = graphql`
    fragment PdpSharedFeaturedRelatedItemsCarouselLazy_user on User {
        ...PdpSharedFeaturedRelatedItemsCarousel_user
    }
`;

const PdpSharedFeaturedRelatedItemsCarouselLazyComponent: FC<Props> = ({
    viewer: viewerRef,
    user: userRef,
    currency,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const containerRef = useRef(null);
    const sponsoredItems = viewer?.sponsoredItems;
    const shouldFireTracking = sponsoredItems?.shouldFireTracking || false;

    return (
        <>
            <div ref={containerRef} />
            <VisibilityTracker
                elementRef={containerRef}
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible && shouldFireTracking) {
                        trackAbTestV2Variant(SPONSORED_LISTING_ON_PDP);
                    }
                }}
            />
            <PdpSharedFeaturedRelatedItemsCarousel
                viewer={viewer}
                sponsoredItems={sponsoredItems}
                user={user}
                currency={currency}
            />
        </>
    );
};

export const PdpSharedFeaturedRelatedItemsCarouselLazy: FC<Props> = ({
    viewer,
    user,
    currency,
    afterDisplay,
}) => {
    const { isBot, isMobile } = useServerVarsContext();
    const isClient = useClientState();

    const loadingComponent =
        !isMobile && isClient && getAbTestV2(SPONSORED_LISTING_ON_PDP)?.variant === 'variant' ? (
            <PdpRespMarketplaceLoadingCarousel />
        ) : null;

    if (isBot) {
        return null;
    }
    if (!afterDisplay) {
        return loadingComponent;
    }

    return (
        <ClientSuspense fallback={loadingComponent}>
            <PdpSharedFeaturedRelatedItemsCarouselLazyComponent
                viewer={viewer}
                user={user}
                currency={currency}
            />
        </ClientSuspense>
    );
};
